import { useState, useEffect } from 'react';
import { Property } from '../types';
import { mockProperties } from '../utils/mockData';
import { propertyService } from '../services/database';

// Global state for properties
let globalProperties: Property[] = mockProperties;
let listeners: (() => void)[] = [];

const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

export const useProperties = () => {
  const [properties, setProperties] = useState<Property[]>(globalProperties);

  useEffect(() => {
    const listener = () => {
      setProperties([...globalProperties]);
    };
    
    listeners.push(listener);
    
    return () => {
      listeners = listeners.filter(l => l !== listener);
    };
  }, []);

  const updateProperty = async (id: string, updates: Partial<Property>) => {
    // Optimistic update: save previous state
    const prevProperties = [...globalProperties];
    globalProperties = globalProperties.map(property => 
      property.id === id 
        ? { ...property, ...updates, updated_at: new Date().toISOString() }
        : property
    );
    notifyListeners();

    // Persist to backend
    try {
      await propertyService.updateProperty(id, updates);
    } catch (error) {
      // Revert on error
      globalProperties = prevProperties;
      notifyListeners();
      throw error;
    }
  };

  const addProperty = (property: Property) => {
    globalProperties = [...globalProperties, property];
    notifyListeners();
  };

  const getProperty = (id: string) => {
    return globalProperties.find(property => property.id === id);
  };

  return {
    properties,
    updateProperty,
    addProperty,
    getProperty
  };
}; 